/**
 * URL Filter Parameters Utility
 *
 * Provides type-safe handling of URL query parameters for filter state.
 * Uses snake_case format following REST API standards.
 *
 * Phase 1: organization_id for partner referrals
 * Future: zip_code, distance, specialty, etc.
 */

// Supported URL filter parameter keys (snake_case)
export const URL_FILTER_PARAMS = {
  ORGANIZATION_ID: 'organization_id',
  // Legacy support - will be deprecated
  ORGANIZATION_ID_LEGACY: 'OrganizationID',
  // Future params (Phase 2+)
  // ZIP_CODE: 'zip_code',
  // DISTANCE: 'distance',
  // SPECIALTY: 'specialty',
} as const

export type UrlFilterParamKey =
  (typeof URL_FILTER_PARAMS)[keyof typeof URL_FILTER_PARAMS]

// localStorage keys for referral data
export const REFERRAL_STORAGE_KEYS = {
  ORGANIZATION: 'referredOrganization',
  // Legacy key for backward compatibility
  ORGANIZATION_LEGACY: 'OrganizationID',
} as const

// Shape of stored referral organization data
export interface ReferredOrganization {
  id: string
  name: string
  validated: boolean
  extractedAt: string // ISO timestamp when extracted from URL
  appliedAt: string | null // ISO timestamp when filter was applied (one-time use)
}

/**
 * Extract organization_id from URL search string
 * Supports both new snake_case and legacy camelCase formats
 *
 * @param searchString - URL search string (e.g., "?organization_id=xxx")
 * @returns organization ID or null if not present
 */
export const extractOrganizationIdFromUrl = (
  searchString: string
): string | null => {
  const params = new URLSearchParams(searchString)

  // Try new format first (snake_case - standard)
  const newFormat = params.get(URL_FILTER_PARAMS.ORGANIZATION_ID)
  if (newFormat) {
    return newFormat
  }

  // Fall back to legacy format (camelCase - deprecated)
  const legacyFormat = params.get(URL_FILTER_PARAMS.ORGANIZATION_ID_LEGACY)
  if (legacyFormat) {
    // eslint-disable-next-line no-console
    console.warn(
      `[urlFilterParams] Using deprecated 'OrganizationID' format. ` +
        `Please update to 'organization_id'. Support will be removed in a future release.`
    )
    return legacyFormat
  }

  return null
}

/**
 * Build URL search string from filter parameters
 * Useful for generating shareable links (future use)
 *
 * @param params - Object with filter parameters
 * @returns URL search string (e.g., "?organization_id=xxx")
 */
export const buildFilterUrlParams = (params: {
  organizationId?: string
  // Future params
  // zipCode?: string
  // distance?: string
  // specialty?: string[]
}): string => {
  const searchParams = new URLSearchParams()

  if (params.organizationId) {
    searchParams.set(URL_FILTER_PARAMS.ORGANIZATION_ID, params.organizationId)
  }

  const result = searchParams.toString()
  return result ? `?${result}` : ''
}

/**
 * Check if organization referral should be applied
 * Returns true only if:
 * - Organization data exists in storage
 * - Organization was validated
 * - Filter hasn't been applied yet (one-time use)
 *
 * @param referralData - Stored referral organization data
 * @returns boolean indicating if filter should be applied
 */
export const shouldApplyOrganizationReferral = (
  referralData: ReferredOrganization | null
): boolean => {
  if (!referralData) return false
  if (!referralData.validated) return false
  if (referralData.appliedAt !== null) return false
  return true
}

/**
 * Mark organization referral as applied (one-time use)
 * Updates the appliedAt timestamp to prevent re-application
 *
 * @param referralData - Current referral data
 * @returns Updated referral data with appliedAt set
 */
export const markOrganizationReferralApplied = (
  referralData: ReferredOrganization
): ReferredOrganization => {
  return {
    ...referralData,
    appliedAt: new Date().toISOString(),
  }
}
